/**
 * cas-853b — the one list of social CDN hosts whose images must be loaded
 * through our public proxy (`GET /api/v1/posts/proxy-image?url=…`) instead of
 * hotlinked.
 *
 * Instagram's CDN (scontent-*.cdninstagram.com) answers with
 * `cross-origin-resource-policy: same-origin` (measured 2026-10-14 on 9/9
 * thumbnails), so Chrome refuses the bytes on gabber.studio with
 * ERR_BLOCKED_BY_RESPONSE.NotSameOrigin. The proxy serves the same image
 * without that header. TikTok and YouTube CDN URLs expire or block CORS too.
 *
 * SmartImage and every raw <img>/<q-img> that may receive one of these URLs
 * resolve their src here, so the host list lives in exactly one place.
 */

const PROXY_PATH = '/api/v1/posts/proxy-image';

const hostIs = (host: string, domain: string) => host === domain || host.endsWith(`.${domain}`);

/** True when `url` is an absolute social-CDN image URL that has to go through the proxy. */
export function isProxiedSocialCdnUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.pathname.startsWith(PROXY_PATH)) return false;

  const host = parsed.hostname.toLowerCase();
  return (
    // Instagram also returns scontent.*.fbcdn.net avatars without "instagram"
    // in their URL. All Facebook CDN images need the same proxy (cas-3023).
    hostIs(host, 'cdninstagram.com') ||
    hostIs(host, 'instagram.com') ||
    hostIs(host, 'fbcdn.net') ||
    // TikTok (tiktokcdn.com, tiktokcdn-us.com, …).
    host.includes('tiktokcdn') ||
    hostIs(host, 'tiktok.com') ||
    // YouTube thumbnails and channel art.
    hostIs(host, 'ytimg.com') ||
    hostIs(host, 'googleusercontent.com') ||
    (hostIs(host, 'youtube.com') && url.toLowerCase().includes('img'))
  );
}

/**
 * The src to put on an image element for `url`: social CDN URLs are rewritten
 * to the proxy on `apiBase`, a relative proxy path gets `apiBase` prepended,
 * and anything else is returned unchanged.
 */
export function socialImageSrc(url: string, apiBase: string | null | undefined): string {
  const base = (apiBase ?? '').replace(/\/+$/, '');
  if (url.startsWith(PROXY_PATH)) return `${base}${url}`;
  if (!isProxiedSocialCdnUrl(url)) return url;
  return `${base}${PROXY_PATH}?url=${encodeURIComponent(url)}`;
}
